import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Coins, Check, Zap, Loader2, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `£${n}`;

export default function Pricing() {
  const { credits } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get("/payments/products")
      .then((r) => setProducts(r.data.products || []))
      .finally(() => setLoading(false));
  }, []);

  const buy = async (lookup_key) => {
    setBuying(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url; // hand off to Stripe Checkout
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBuying(null);
    }
  };

  const tiers = products.filter((p) => p.tier);
  const topups = products.filter((p) => p.type === "topup");

  if (loading) return <p className="text-muted-foreground">Loading plans…</p>;

  return (
    <div className="space-y-12" data-testid="pricing-page">
      <div className="fade-up">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Coins size={24} />
          </div>
          <div>
            <h1 className="font-head text-4xl font-bold">Buy credits</h1>
            <p className="text-muted-foreground">
              Credits power every AI generation. Pitch Test & Course Outline cost 5, full courses & content packs cost 15.
            </p>
          </div>
        </div>
        {credits && (
          <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-sm font-semibold"
             data-testid="pricing-current-balance">
            {credits.unlimited ? <><InfinityIcon size={15} /> Unlimited (admin)</>
              : <><Coins size={15} className="text-primary" /> Current balance: {credits.balance} credits</>}
          </p>
        )}
      </div>

      {/* Tiers */}
      <section>
        <h2 className="mb-5 font-head text-lg font-semibold">Plans</h2>
        <div className="grid gap-5 md:grid-cols-3">
          {tiers.map((p) => {
            const isSub = p.type === "subscription";
            const highlight = p.lookup_key === "pro_monthly";
            return (
              <Card key={p.lookup_key} data-testid={`plan-${p.lookup_key}`}
                className={`relative flex flex-col p-6 transition-transform duration-200 hover:-translate-y-1 ${
                  highlight ? "border-primary ring-2 ring-primary shadow-lg" : "border-border"
                }`}>
                {highlight && (
                  <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
                    Most credits
                  </span>
                )}
                <h3 className="font-head text-2xl font-bold">{p.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-head text-4xl font-bold">{money(p.price)}</span>
                  <span className="mb-1 text-sm text-muted-foreground">{isSub ? "/month" : "one-off"}</span>
                </div>
                <div className="mt-4 flex items-center gap-2 rounded-lg bg-secondary/60 px-3 py-2 text-sm font-semibold">
                  <Zap size={15} className="text-primary" />
                  {p.credits} credits{isSub ? " every month" : ""}
                </div>
                <ul className="mt-4 flex-1 space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Check size={15} className="mt-0.5 shrink-0 text-accent" /> ~{Math.floor(p.credits / 5)} Pitch Tests or {Math.floor(p.credits / 15)} full courses</li>
                  <li className="flex items-start gap-2"><Check size={15} className="mt-0.5 shrink-0 text-accent" /> Use across all your offers</li>
                  {isSub && <li className="flex items-start gap-2"><Check size={15} className="mt-0.5 shrink-0 text-accent" /> Auto-renews monthly, cancel anytime</li>}
                  {highlight && (
                    <li className="flex items-start gap-2" data-testid="pro-unlimited-journeys">
                      <Check size={15} className="mt-0.5 shrink-0 text-accent" />
                      <span><span className="font-semibold text-foreground">Unlimited Guided journeys</span> — no limit on how many journeys you can start. (This is not unlimited credits; each AI step still uses credits.)</span>
                    </li>
                  )}
                </ul>
                {highlight && (
                  <p className="mt-3 rounded-lg bg-secondary/60 px-3 py-2 text-xs font-medium text-muted-foreground"
                     data-testid="pro-credits-note">
                    Credits included: 300/month. Top up anytime.
                  </p>
                )}
                <Button onClick={() => buy(p.lookup_key)} disabled={buying === p.lookup_key}
                  data-testid={`buy-${p.lookup_key}`}
                  className={`mt-6 w-full rounded-full ${highlight ? "bg-primary hover:bg-primary/90" : ""}`}
                  variant={highlight ? "default" : "outline"}>
                  {buying === p.lookup_key ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : null}
                  {isSub ? "Subscribe" : "Buy now"}
                </Button>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Top-ups */}
      <section>
        <h2 className="mb-2 font-head text-lg font-semibold">Top-up packs</h2>
        <p className="mb-5 text-sm text-muted-foreground">One-off credit packs, available on any plan.</p>
        <div className="grid gap-4 sm:grid-cols-3">
          {topups.map((p) => (
            <Card key={p.lookup_key} data-testid={`topup-${p.lookup_key}`}
              className="flex items-center justify-between border-border p-5 transition-transform duration-200 hover:-translate-y-1">
              <div>
                <p className="font-head text-xl font-bold">{p.credits} credits</p>
                <p className="text-sm text-muted-foreground">{money(p.price)} one-off</p>
              </div>
              <Button size="sm" onClick={() => buy(p.lookup_key)} disabled={buying === p.lookup_key}
                data-testid={`buy-${p.lookup_key}`} className="rounded-full bg-primary hover:bg-primary/90">
                {buying === p.lookup_key ? <Loader2 size={15} className="animate-spin" /> : "Buy"}
              </Button>
            </Card>
          ))}
        </div>
      </section>

      <p className="text-xs text-muted-foreground">
        Secure checkout by Stripe. Test mode — use card 4242 4242 4242 4242, any future expiry, any CVC.
      </p>
    </div>
  );
}
